/**
 * redirectPreview — pure helpers that MIRROR the `go` redirect function's URL +
 * UTM logic (clozesureiodev/functions/handlers/redirects.js) so the Create-
 * Redirect UI previews exactly what a real visit will produce.
 *
 * ⚠️ Keep in sync with that function: normalizeCampaignId, getCampaignMedium,
 * parseSubdomainRedirect, and generateRedirectUrl's utm_campaign derivation.
 */

const GO_BASE = "https://go.clozesure.com";

/**
 * Stored campaignId form (mirror of normalizeCampaignId's `normalized`):
 * lowercase; spaces/+ → hyphens; other chars → hyphens; collapse + trim hyphens.
 * Underscores are preserved.
 */
export function normalizeCampaignId(raw) {
  if (!raw) return "";
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch (e) {
    decoded = raw;
  }
  return decoded
    .toLowerCase()
    .replace(/\+/g, "-")
    .replace(/%20/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-_]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * utm_campaign: everything after the FIRST hyphen of the stored campaignId
 * (or the whole id when there's no hyphen). e.g. "168781023-d2c-launch" →
 * "d2c-launch"; "web-rightmove" → "rightmove".
 */
export function utmCampaignOf(campaignId) {
  if (!campaignId) return "";
  return campaignId.includes("-")
    ? campaignId.split("-").slice(1).join("-")
    : campaignId;
}

/** Mirror of getCampaignMedium (the intelligent default when no override). */
export function deriveMedium(campaignId, campaignType) {
  const id = (campaignId || "").toLowerCase();
  const type = (campaignType || "").toLowerCase();
  if (id.includes("negotiator") || id.includes("mailshot")) return "email";
  // "youtube": go 29a4a57 (Lee, 1 Nov 2026): a YouTube link is guessed social; video is set by hand.
  if (["instagram", "linkedin", "facebook", "twitter", "social", "youtube"].includes(type))
    return "social";
  if (
    ["google", "paid-search", "ppc"].includes(type) ||
    id.includes("google") ||
    id.includes("keywords")
  )
    return "cpc";
  if (
    ["booklet", "window-sticker", "poster"].includes(type) ||
    id.includes("pos") ||
    id === "160884927"
  )
    return "print";
  if (
    ["pt", "jt", "test"].includes(type) ||
    id.includes("compcard") ||
    id.includes("promoters-")
  )
    return "qr";
  return "qr";
}

/**
 * Parse a landing path that may target a subdomain (mirror of
 * parseSubdomainRedirect). "@assets/brochure" → {subdomain:"assets",
 * path:"/brochure"}; "conveyancers" → {subdomain:"www", path:"/conveyancers"}.
 */
export function parseLandingPath(raw) {
  const clean = (raw || "").trim().replace(/^\/+|\/+$/g, "");
  if (!clean) return { subdomain: "www", path: "" };
  const parts = clean.split("/");
  const first = parts[0];
  if (first.startsWith("@")) {
    const sub = first.slice(1).toLowerCase();
    if (!/^[a-z0-9]+$/.test(sub)) {
      // Invalid subdomain → go falls back to treating it all as a www path.
      return { subdomain: "www", path: "/" + clean };
    }
    const rest = parts.slice(1).join("/");
    return { subdomain: sub, path: rest ? "/" + rest : "" };
  }
  return { subdomain: "www", path: "/" + clean };
}

/**
 * The canonical go URL. A landing path needs a source segment to sit after, so
 * a path with no explicit source falls back to "default" (which is what the go
 * function uses for a missing source anyway).
 */
export function buildGoUrl(campaignId, source, landingPath) {
  const src = (source || "").trim();
  const path = (landingPath || "").trim().replace(/^\/+|\/+$/g, "");
  let url = `${GO_BASE}/hs/${campaignId}`;
  const seg = src || (path ? "default" : "");
  if (seg) url += `/${seg}`;
  if (path) url += `/${path}`;
  return url;
}

/**
 * The final destination + UTMs the go function will produce, so the UI preview
 * matches reality. `mediumOverride` (if provided) wins over the derived medium.
 */
export function buildDestination(campaignId, source, landingPath, mediumOverride) {
  const { subdomain, path } = parseLandingPath(landingPath);
  const base =
    subdomain === "www"
      ? "https://www.clozesure.com"
      : `https://${subdomain}.clozesure.com`;
  const effectiveSource = (source || "").trim() || "default";
  const utm = {
    utm_campaign: utmCampaignOf(campaignId),
    utm_source: effectiveSource,
    utm_medium:
      mediumOverride && mediumOverride.trim()
        ? mediumOverride.trim()
        : deriveMedium(campaignId, effectiveSource),
  };
  const qs = new URLSearchParams(utm).toString();
  return { url: `${base}${path || ""}?${qs}`, utm, subdomain, path };
}

/**
 * Generate a collision-safe, non-numeric campaign identifier prefix for a
 * standalone campaign (one NOT tied to a HubSpot numeric id). Non-numeric so it
 * can never clash with HubSpot's numeric campaign ids. Shape: cz<yymmdd><2rand>.
 * The friendly name is appended by the caller as "<prefix>-<friendly>".
 */
export function generateCampaignPrefix(date = new Date()) {
  const yy = String(date.getFullYear()).slice(2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const rand = Math.random().toString(36).slice(2, 4);
  return `cz${yy}${mm}${dd}${rand}`;
}
